import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { UsersRound, Plus, Pencil, Trash2, Loader2, Search, Save, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function UserGroupsManager({ token }) {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [memberIds, setMemberIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [groupsRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/user-groups`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/users`, { headers }),
      ]);
      setGroups(groupsRes.data.groups || []);
      setUsers(usersRes.data || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectGroup = (group) => {
    setSelectedGroup(group);
    setMemberIds(new Set(group.user_ids || []));
  };

  const toggleMember = (userId) => {
    setMemberIds(prev => {
      const n = new Set(prev);
      n.has(userId) ? n.delete(userId) : n.add(userId);
      return n;
    });
  };

  const saveMembers = async () => {
    if (!selectedGroup) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/user-groups/${selectedGroup.id}`, {
        user_ids: Array.from(memberIds),
      }, { headers });
      toast.success('Group members updated');
      fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const openCreate = () => {
    setEditForm({ name: '', description: '' });
    setEditDialog(true);
  };

  const createGroup = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/user-groups`, editForm, { headers });
      toast.success('Group created');
      setEditDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/user-groups/${groupId}`, { headers });
      toast.success('Group deleted');
      if (selectedGroup?.id === groupId) setSelectedGroup(null);
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const filteredUsers = useMemo(() => {
    if (!userSearch) return users;
    const q = userSearch.toLowerCase();
    return users.filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  }, [users, userSearch]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="flex h-full" data-testid="user-groups-manager">
      {/* Group List */}
      <div className="w-64 border-r bg-muted/20 flex flex-col shrink-0">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">User Groups</h3>
            <Button variant="outline" size="sm" onClick={openCreate} data-testid="create-group-btn" className="h-7 gap-1 text-xs">
              <Plus className="h-3 w-3" /> New
            </Button>
          </div>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {groups.map(g => (
              <button key={g.id} onClick={() => selectGroup(g)} data-testid={`group-${g.id}`}
                className={`w-full text-left p-3 rounded-lg transition-colors text-sm ${
                  selectedGroup?.id === g.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/60'
                }`}>
                <div className="font-medium truncate">{g.name}</div>
                <span className="text-[10px] text-muted-foreground">{g.member_count || 0} members</span>
              </button>
            ))}
            {groups.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No groups yet</p>}
          </div>
        </ScrollArea>
      </div>

      {/* Members */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {selectedGroup ? (
          <>
            <div className="p-5 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{selectedGroup.name}</h2>
                  {selectedGroup.description && <p className="text-xs text-muted-foreground">{selectedGroup.description}</p>}
                  <Badge variant="secondary" className="text-[10px] mt-1">{memberIds.size} members selected</Badge>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveMembers} disabled={saving} size="sm" className="gap-1 text-xs">
                    <Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save Members'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => deleteGroup(selectedGroup.id)} className="gap-1 text-xs text-destructive hover:text-destructive">
                    <Trash2 className="h-3 w-3" /> Delete
                  </Button>
                </div>
              </div>
              <div className="relative mt-3 max-w-sm">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="pl-8 h-8 text-xs" />
              </div>
            </div>

            <ScrollArea className="flex-1 p-5">
              <div className="space-y-1 max-w-xl">
                {filteredUsers.map(u => (
                  <label key={u.id} className="flex items-center gap-3 p-2.5 rounded-lg border hover:bg-muted/30 cursor-pointer">
                    <Checkbox checked={memberIds.has(u.id)} onCheckedChange={() => toggleMember(u.id)} />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium">{u.name || 'Unnamed'}</span>
                      <span className="text-xs text-muted-foreground ml-2">{u.email}</span>
                    </div>
                    <Badge variant="outline" className="text-[9px]">{u.role}</Badge>
                  </label>
                ))}
                {filteredUsers.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No users found</p>}
              </div>
            </ScrollArea>
          </>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <UsersRound className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a group to manage members</p>
              <p className="text-xs mt-1">or create a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Group Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create User Group</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                placeholder="All Management Users" className="mt-1" data-testid="group-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                placeholder="All users with management access" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={createGroup} disabled={saving || !editForm.name}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
